import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'fa';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/about#webpage`,
        url: `${siteUrl}/about`,
        name: 'About Us - Loveish Group',
        description: 'Learn about Loveish Group, our mission, values, and commitment to providing premium food products and digital services.',
        isPartOf: {
          '@id': `${siteUrl}/#website`
        },
        breadcrumb: {
          '@id': `${siteUrl}/about#breadcrumb`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/about#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'About',
            item: `${siteUrl}/about`
          }
        ]
      },
      {
        '@type': 'AboutPage',
        '@id': `${siteUrl}/about#aboutpage`,
        url: `${siteUrl}/about`,
        name: 'About Loveish Group',
        description: 'Discover our story, mission, and the values that drive us to deliver exceptional products and services.',
        mainEntity: {
          '@id': `${siteUrl}/#organization`
        }
      }
    ]
  };

  useSEO({
    title: 'About Us - Loveish Group',
    description: 'Learn about Loveish Group, our mission, values, and commitment to providing premium food products and digital services.',
    keywords: 'about Loveish Group, our story, company values, mission',
    canonical: `${siteUrl}/about`,
    schema: aboutSchema
  });

  // Falling trust symbols animation
  const fallingSymbols = useMemo(() => {
    const symbols = ['🏆', '⭐', '✅', '🎖️', '💎', '🛡️', '👍', '🤝', '💯', '✨', '🌟', '🏅'];
    const isMobile = window.innerWidth < 768;
    const symbolCount = isMobile ? 6 : 12;
    
    return Array.from({ length: symbolCount }, (_, i) => ({
      emoji: symbols[i % symbols.length],
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 5}s`,
      duration: `${8 + Math.random() * 8}s`,
      size: isMobile ? `${15 + Math.random() * 15}px` : `${20 + Math.random() * 20}px`
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ced05crgmf5papdlpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Animated Trust Symbols */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
        {fallingSymbols.map((item, index) => (
          <div
            key={index}
            className="absolute animate-fall-fruit"
            style={{
              left: item.left,
              fontSize: item.size,
              animationDuration: item.duration,
              animationDelay: item.delay,
              willChange: 'transform, opacity',
              transform: 'translate3d(0, 0, 0)',
            } as React.CSSProperties}
          >
            {item.emoji}
          </div>
        ))}
      </div>

      <Header />
      
      {/* Hero Section */}
      <section className="pt-28 md:pt-32 lg:pt-36 pb-12 md:pb-16 lg:pb-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            {t('aboutTitle')}
          </h1>
          <p className={`text-base md:text-lg lg:text-xl text-emerald-100 max-w-3xl mx-auto leading-relaxed px-4 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('aboutDescription')}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${isRTL ? 'lg:grid-flow-col-dense' : ''}`}>
            <div className={`${isRTL ? 'lg:col-start-2 text-right' : 'text-left'}`}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                {t('heroSlogan')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                {t('aboutDescription')}
              </p>
              <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                {t('heroDescription')}
              </p>
              <div className="space-y-3 md:space-y-4">
                <div className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <i className={`ri-check-double-line text-emerald-600 text-lg md:text-xl ${isRTL ? 'ml-3' : 'mr-3'}`}></i>
                  <span className="text-sm md:text-base text-gray-700 font-medium">Premium Quality Products</span>
                </div>
                <div className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <i className={`ri-check-double-line text-emerald-600 text-lg md:text-xl ${isRTL ? 'ml-3' : 'mr-3'}`}></i>
                  <span className="text-sm md:text-base text-gray-700 font-medium">Traditional Authentic Recipes</span>
                </div>
                <div className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <i className={`ri-check-double-line text-emerald-600 text-lg md:text-xl ${isRTL ? 'ml-3' : 'mr-3'}`}></i>
                  <span className="text-sm md:text-base text-gray-700 font-medium">Carefully Selected Ingredients</span>
                </div>
                <div className={`flex items-center ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <i className={`ri-check-double-line text-emerald-600 text-lg md:text-xl ${isRTL ? 'ml-3' : 'mr-3'}`}></i>
                  <span className="text-sm md:text-base text-gray-700 font-medium">Made with Love and Care</span>
                </div>
              </div>
            </div>
            <div className={`${isRTL ? 'lg:col-start-1' : ''}`}>
              <img
                src="https://public.readdy.ai/ai/img_res/f35556f4fcb44f62b8e7d28b743d2fe6.jpg"
                alt="About Loveish"
                className="w-full h-64 md:h-80 lg:h-96 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className={`text-center mb-10 md:mb-16 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">Our Values</h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              What drives us to deliver the best products to your table
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className={`text-center p-6 md:p-8 bg-white rounded-2xl shadow-lg ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-heart-line text-emerald-600 text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Quality First</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                We source only the finest ingredients and maintain the highest quality standards in every product we offer.
              </p>
            </div>

            <div className={`text-center p-6 md:p-8 bg-white rounded-2xl shadow-lg ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-leaf-line text-emerald-600 text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Natural & Pure</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                All our products are made with natural ingredients, free from artificial preservatives and additives.
              </p>
            </div>

            <div className={`text-center p-6 md:p-8 bg-white rounded-2xl shadow-lg ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-global-line text-emerald-600 text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Cultural Heritage</h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                We preserve and share the rich culinary traditions of Persian and Middle Eastern cuisine with the world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className={`text-center mb-10 md:mb-16 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">Our Story</h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              From an expert and specialist in the food standards organization and laboratory to a small family business and building a reputable brand, our journey has been full of passion for authentic flavors. That which, in addition to health, gives people joy and love.
            </p>
          </div>

          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${isRTL ? 'lg:grid-flow-col-dense' : ''}`}>
            <div className={`${isRTL ? 'lg:col-start-1' : ''}`}>
              <img
                src="https://readdy.ai/api/search-image?query=Traditional%20Middle%20Eastern%20spice%20market%20with%20colorful%20displays%20of%20nuts%20dried%20fruits%20and%20sweets%2C%20bustling%20bazaar%20atmosphere%2C%20warm%20golden%20lighting%2C%20authentic%20cultural%20food%20market%20scene&width=600&height=400&seq=32&orientation=landscape"
                alt="Our Story"
                className="w-full h-64 md:h-80 lg:h-96 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
            <div className={`${isRTL ? 'lg:col-start-2 text-right' : 'text-left'}`}>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
                Three Generations of Excellence
              </h3>
              <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                Our family has been in the food business for over 60 years, passing down traditional recipes and techniques from generation to generation. What started as a small local shop has grown into a beloved brand that serves customers worldwide.
              </p>
              <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                Every product we create carries the wisdom of our ancestors and the innovation of modern food science, ensuring that you receive the perfect balance of tradition and quality.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Today, we continue to honor our heritage while embracing new technologies and sustainable practices to bring you the finest selection of Middle Eastern delicacies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('getInTouch')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                {t('contactDescription')}
              </p>
            </div>

            <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 md:p-8 lg:p-12">
              <form className="space-y-5 md:space-y-6" data-readdy-form id="about-contact-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <div>
                    <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('firstName')}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                      placeholder={t('firstNamePlaceholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('lastName')}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                      placeholder={t('lastNamePlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('subjectPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('message')}
                  </label>
                  <textarea
                    name="message"
                    rows={6}
                    maxLength={500}
                    className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-sm resize-none ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('messagePlaceholder')}
                    required
                  ></textarea>
                  <p className={`text-xs text-gray-500 mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('maxCharacters')}
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-emerald-50 border-2 border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-fade-in">
                    <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="font-semibold">{t('messageSentSuccess')}</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-fade-in">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-white text-sm"></i>
                    </div>
                    <span className="font-semibold">{t('messageSentError')}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-4 px-6 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-emerald-500/50 hover:scale-[1.02] whitespace-nowrap cursor-pointer text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('sending')}</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-xl"></i>
                      <span>{t('sendMessage')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}